/**
 * The one place a request reaches Ceedero API.
 *
 * Every rule the Web Session depends on lives here rather than at a call site,
 * because each of them fails silently when forgotten: a request without
 * `credentials: "include"` is anonymous and reads as signed out, a
 * state-changing request without the Web application's header is refused as
 * forgery, and a refusal read as a sentence rather than a code is a message
 * this application cannot translate.
 *
 * There is deliberately no client-side "am I signed in" flag anywhere. The
 * session cookie is `HttpOnly` and unreadable, and the Sign-in Hint carries no
 * authority — it exists for the Landing page. Signed-in-ness is discovered by
 * asking for the profile, and a `401` from anywhere means "sign in again".
 */

/** The header that separates a request this application made from one a hostile page caused. */
const WEB_REQUEST_HEADER = "X-Ceedero-Web";

const STATE_CHANGING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/**
 * Every refusal this application acts on differently. Anything else is an
 * outage: guessing at an unrecognised code would tell a user something specific
 * and possibly untrue about their own account.
 */
export type CeederoApiRefusal =
  | "invalid_credentials"
  | "not_entitled"
  | "rate_limited"
  | "unauthorized"
  | "csrf_failed"
  | "not_found"
  | "username_taken"
  | "invalid_avatar"
  | "payload_too_large"
  | "invalid_recovery_code"
  | "account_unclaimed"
  | "unavailable";

export class CeederoApiError extends Error {
  constructor(
    readonly refusal: CeederoApiRefusal,
    readonly status: number,
  ) {
    super(`Ceedero API request failed: ${refusal}.`);
  }
}

const KNOWN_REFUSALS = new Set<string>([
  "invalid_credentials",
  "not_entitled",
  "rate_limited",
  "unauthorized",
  "csrf_failed",
  "not_found",
  "username_taken",
  "invalid_avatar",
  "payload_too_large",
  "invalid_recovery_code",
  "account_unclaimed",
]);

const readRefusal = async (response: Response): Promise<CeederoApiRefusal> => {
  const body: unknown = await response.json().catch(() => undefined);
  const code =
    typeof body === "object" &&
    body !== null &&
    "error" in body &&
    typeof body.error === "object" &&
    body.error !== null &&
    "code" in body.error &&
    typeof body.error.code === "string"
      ? body.error.code
      : undefined;
  return code !== undefined && KNOWN_REFUSALS.has(code)
    ? (code as CeederoApiRefusal)
    : "unavailable";
};

export interface CeederoApiRequest {
  /**
   * JSON unless it is a `Blob`, which is sent as its own bytes under its own
   * media type. An avatar is the only such body, and routing it through a
   * second transport would be a second place for the cookie, the forgery
   * header and the refusal decoding to be forgotten.
   */
  body?: unknown;
  method?: string;
  path: string;
  signal?: AbortSignal;
}

export interface CeederoApi {
  request: <Result>(request: CeederoApiRequest) => Promise<Result>;
}

export const createCeederoApi = ({
  origin,
  /**
   * Resolved at call time rather than captured at construction.
   *
   * `send = fetch` binds whatever `fetch` was when this module was first
   * evaluated, which is before a test, a mock server or a service worker has
   * had any chance to install one — so the transport became the one thing in
   * the application that could never be intercepted.
   */
  send = (input, init) => globalThis.fetch(input, init),
}: {
  origin: string;
  send?: typeof fetch;
}): CeederoApi => {
  const base = origin.replace(/\/$/, "");

  const request = async <Result>({
    body,
    method = "GET",
    path,
    signal,
  }: CeederoApiRequest): Promise<Result> => {
    const isBinary = body instanceof Blob;
    const headers = new Headers();
    if (body !== undefined) {
      // A Blob names its own type, and the API reads that header to decide
      // whether it will accept the bytes at all.
      headers.set("content-type", isBinary ? body.type : "application/json");
    }
    // Sent on exactly the requests the API requires it on. A cookie is ambient
    // authority the browser attaches to requests any page can cause; this
    // header is what a cross-origin form cannot set.
    if (STATE_CHANGING_METHODS.has(method.toUpperCase())) {
      headers.set(WEB_REQUEST_HEADER, "1");
    }

    let response: Response;
    try {
      response = await send(`${base}${path}`, {
        method,
        headers,
        // Without this the browser sends no cookie and every request reads as
        // signed out — the single most consequential line in this module.
        credentials: "include",
        ...(body === undefined ? {} : { body: isBinary ? body : JSON.stringify(body) }),
        ...(signal ? { signal } : {}),
      });
    } catch {
      // `fetch` reports every transport-level failure the same way, and they
      // all mean the same thing to somebody looking at a page that did not
      // load.
      throw new CeederoApiError("unavailable", 0);
    }

    if (!response.ok) {
      throw new CeederoApiError(await readRefusal(response), response.status);
    }
    // A `204` carries no body, and neither does the `202` a recovery request
    // is answered with; asking for JSON from either throws.
    if (response.status === 204 || response.status === 202) {
      return undefined as Result;
    }
    return (await response.json()) as Result;
  };

  return { request };
};
